import React, { useCallback } from 'react';
import { editTable } from 'roosterjs-editor-api';
import { TableOperation } from 'roosterjs-editor-types';
import styled, { useTheme } from 'styled-components';
import { FullWidthStackPanel } from './common';
import { Typography } from '../../../../../typography';
import { DefaultButton, IButtonProps } from '../../../../../button';
import { useToolbarActionContext } from '../../../context';
import { Separator } from '../../../../../separator';

interface IActionProps extends Pick<IButtonProps, 'iconName' | 'iconSize' | 'className' | 'elementRef'> {
    label: string;
    operation: TableOperation;
    onClick(): void;
}

function ActionBase(props: IActionProps) {
    const { onClick, operation, ...rest } = props;
    const { editor } = useToolbarActionContext();
    const _onClick = useCallback(() => {
        editTable(editor, operation);
        onClick();
    }, [editor, onClick, operation]);
    return <DefaultButton {...rest} size="small" onClick={_onClick} />;
}

const Action = styled(ActionBase)`
    font-size: 12px;

    > *:not(:last-child) {
        margin-right: 8px;
    }

    .ap-icon {
        margin-left: 14px;
    }

    .ap-button-label {
        margin-right: 14px;
    }
`;

interface IEditTableConfigProps {
    onInsert(): void;
}

export const EditTableConfig = ({ onInsert }: IEditTableConfigProps) => {
    const { semantics: { palette } } = useTheme();
    return (
        <FullWidthStackPanel orientation="vertical">
            <FullWidthStackPanel orientation="vertical">
                <Typography.H6>Insert Row/Column</Typography.H6>
                <FullWidthStackPanel orientation="horizontal" gap="8">
                    <Action iconName="ap-above.rte" key="above" label="Above" operation={TableOperation.InsertAbove} onClick={onInsert} />
                    <Action iconName="ap-below.rte" key="below" label="Below" operation={TableOperation.InsertBelow} onClick={onInsert} />
                    <Action iconName="ap-left.rte" key="left" label="Left" operation={TableOperation.InsertLeft} onClick={onInsert} />
                    <Action iconName="ap-right.rte" key="right" label="Right" operation={TableOperation.InsertRight} onClick={onInsert} />
                </FullWidthStackPanel>
            </FullWidthStackPanel>
            <Separator orientation="horizontal" hideBeak color={palette.neutralLighter} />
            <FullWidthStackPanel orientation="vertical">
                <Typography.H6>Delete</Typography.H6>
                <FullWidthStackPanel orientation="horizontal" gap="8">
                    <Action iconName="ap-delete-table.rte" key="table" label="Table" operation={TableOperation.DeleteColumn} onClick={onInsert} />
                    <Action iconName="ap-delete-column.rte" key="column" label="Column" operation={TableOperation.DeleteColumn} onClick={onInsert} />
                    <Action iconName="ap-delete-row.rte" key="row" label="Row" operation={TableOperation.DeleteRow} onClick={onInsert} />
                </FullWidthStackPanel>
            </FullWidthStackPanel>
            <Separator orientation="horizontal" hideBeak color={palette.neutralLighter} />
            <FullWidthStackPanel orientation="vertical">
                <Typography.H6>Merge</Typography.H6>
                <FullWidthStackPanel orientation="horizontal" gap="8">
                    <Action iconName="ap-merge-above.rte" key="above" label="Above" operation={TableOperation.MergeAbove} onClick={onInsert} />
                    <Action iconName="ap-merge-below.rte" key="below" label="Below" operation={TableOperation.MergeBelow} onClick={onInsert} />
                    <Action iconName="ap-merge-left.rte" key="left" label="Left" operation={TableOperation.MergeLeft} onClick={onInsert} />
                    <Action iconName="ap-merge-right.rte" key="right" label="Right" operation={TableOperation.MergeRight} onClick={onInsert} />
                </FullWidthStackPanel>
            </FullWidthStackPanel>
            <Separator orientation="horizontal" hideBeak color={palette.neutralLighter} />
            <FullWidthStackPanel orientation="vertical">
                <Typography.H6>Split</Typography.H6>
                <FullWidthStackPanel orientation="horizontal" gap="8">
                    <Action iconName="ap-split-horizontally.rte" key="horizontal" label="Horizontally" operation={TableOperation.SplitHorizontally} onClick={onInsert} />
                    <Action iconName="ap-split-vertically.rte" key="vertical" label="Vertically" operation={TableOperation.SplitVertically} onClick={onInsert} />
                </FullWidthStackPanel>
            </FullWidthStackPanel>
        </FullWidthStackPanel>
    );
};
